import React, {useEffect} from 'react';
import styled from 'styled-components';
import {
   ControlButton,
   Glyphs,
   TextInput,
   TscButtonType, TscStyles, TscThemeName,
   TscThemeNames
} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import {IMediaChapters} from '../../model/mediaDetails';
import {mediaStore} from '../../context/mediaProvider';
import TimeFormatter from '../../util/TimeFormatter';
import Constants from '../../constants/Constants';
import {importMediaInterface} from '../../hooks/dynamicImport';

export enum MediaDetailGenerationTypes {
   title = 'title',
   description = 'description',
   chapters = 'chapters'
}

const MediaDetailsChaptersContainer = styled.div`
   position: relative;
   display: flex;
   flex-direction: column;
   box-sizing: border-box;
`;

const ChaptersInputContainer = styled.div<{theme: TscThemeNames}>`
   display: grid;
   grid-template-columns: 5rem 1fr 2rem;
   grid-column-gap: .5rem;
   grid-row-gap: .5rem;
   padding: .5rem;
   background-color: ${props => props.theme === TscThemeName.dawn ? TscStyles.color.ui.dawn.lightMedium : TscStyles.color.ui.dusk.mediumDark};
   border-radius: ${TscStyles.border.radius.lg};
   border: 1px solid ${props => props.theme === TscThemeName.dawn ? TscStyles.color.ui.dawn.medium : TscStyles.color.ui.dusk.dark};

   .chapterTimeCodeLabel {
      color: ${props => props.theme === TscThemeName.dawn ? TscStyles.color.text.medium : TscStyles.color.text.light};
   }

   .chapterDescriptionLabel {
      grid-column: 2 / 4;
      color: ${props => props.theme === TscThemeName.dawn ? TscStyles.color.text.medium : TscStyles.color.text.light};
   }

  .invalidChapterInput {
    input {
      border: 1px solid ${TscStyles.color.message.warning} !important;
    }
  }

   .addChapterButton {
      grid-column: 1 / 4;
   }
`;

const ChaptersItemContainer = styled.div`
   display: grid;
   grid-template-columns: 5rem 1fr 2rem;
   grid-column-gap: .5rem;
   grid-row-gap: .5rem;
   grid-column: 1 / 4;
   max-height: 10.25rem;
   overflow-y: auto;
   padding: 0.25rem;
   margin: 0 -0.25rem;
`;

const InputErrorText = styled.div`
   font-size: .875rem;
   font-style: normal;
   font-weight: 400;
   line-height: 20px;
   color: ${TscStyles.color.message.warning};
`;

export interface IChaptersDisplayModel {
   timeCode: string;
   description: string;
   timeCodeValid: boolean;
   descriptionValid: boolean;
}

const initialChapter: IChaptersDisplayModel = {
   timeCode: '00:00',
   description: '',
   timeCodeValid: true,
   descriptionValid: true
};

const newChapter: IChaptersDisplayModel = {
   timeCode: '',
   description: '',
   timeCodeValid: true,
   descriptionValid: true
};

const numbersAndColonRegex = /^[0-9:]*$/;

export const MediaDetailsChaptersBase: React.FC<IMediaDetailsChaptersBaseProps & IStateMappedProps> = ({generatedChapters, onChaptersUpdateCallback, onChaptersInputErrorCallback, Duration, Chapters}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [internalChapters, setInternalChapters] = React.useState<IChaptersDisplayModel[]>([]);
   const [chapterIndexJustAdded, setChapterIndexJustAdded] = React.useState<number>(-1);
   const [timeCodeInErrorState, setTimeCodeInErrorState] = React.useState<boolean>(false);
   const [descriptionInErrorState, setDescriptionInErrorState] = React.useState<boolean>(false);

   useEffect(() => {
      if (generatedChapters.length > 0) {
         setInternalChapters(generatedChapters);
         validateChaptersInputs();
      }
   }, [generatedChapters]);

   const onChapterTimeCodeChange = (chapterIndex: number, newTimeCodeString: string): void => {
      const isValidInput = numbersAndColonRegex.test(newTimeCodeString);
      if (!isValidInput) {
         return;
      }
      const copyOfChapters = internalChapters.slice();
      copyOfChapters[chapterIndex].timeCode = newTimeCodeString;
      copyOfChapters[chapterIndex].timeCodeValid = true;
      updateChaptersState(copyOfChapters);
   };

   const updateChaptersState = (newChapters: IChaptersDisplayModel[]): void => {
      setInternalChapters(newChapters);
      onChaptersUpdateCallback(newChapters);
   };

   const onChapterLabelChange = (chapterIndex: number, newLabelString: string): void => {
      const copyOfChapters = internalChapters.slice();
      copyOfChapters[chapterIndex].description = newLabelString;
      copyOfChapters[chapterIndex].descriptionValid = true;
      updateChaptersState(copyOfChapters);
   };

   const addNewChapter = () => {
      const copyOfChapters = internalChapters.slice();
      copyOfChapters.push(copyOfChapters.length === 0 ? {...initialChapter} : {...newChapter});
      setChapterIndexJustAdded(copyOfChapters.length - 1);
      updateChaptersState(copyOfChapters);
   };

   const isTimeCodeValid = (timeCode: string): boolean => {
      const validTimeCode = Constants.timeCodeWithNoSpaceRegex.test(timeCode);
      const timeCodeWithinDuration = TimeFormatter.getSecondsFromTimeString(timeCode) < TimeFormatter.getSecondsFromTimeString(Duration);
      return validTimeCode && timeCodeWithinDuration;
   };

   const validateChaptersInputs = () => {
      const noTimeCodeInputErrors = internalChapters.every(chapter => chapter.timeCodeValid);
      const noDescriptionInputErrors = internalChapters.every(chapter => chapter.descriptionValid);
      setTimeCodeInErrorState(!noTimeCodeInputErrors);
      setDescriptionInErrorState(!noDescriptionInputErrors);
   };

   const removeChapter = (chapterIndex: number) => {
      const copyOfChapters = internalChapters.slice();
      copyOfChapters.splice(chapterIndex, 1);

      // enforce that the first chapter always has a time-code of 00:00
      if (copyOfChapters.length > 0) {
         copyOfChapters[0].timeCode = initialChapter.timeCode;
      }

      updateChaptersState(copyOfChapters);
   };

   const validateBlurredTimeCodeInput = (chapterIndex: number) => {
      setInternalChapters(internalChapters.map((chapter, index) => {
         if (index === chapterIndex) {
            return {...chapter, timeCodeValid: isTimeCodeValid(chapter.timeCode)};
         }
         return chapter;
      }));
   };

   const validateBlurredDescriptionInput = (chapterIndex: number) => {
      setInternalChapters(internalChapters.map((chapter, index) => {
         if (index === chapterIndex) {
            return {...chapter, descriptionValid: !!chapter.description};
         }
         return chapter;
      }));
   };

   useEffect(() => {
      validateChaptersInputs();
   }, [internalChapters]);

   useEffect(() => {
      onChaptersInputErrorCallback(timeCodeInErrorState || descriptionInErrorState);
   }, [timeCodeInErrorState, descriptionInErrorState]);

   useEffect(() => {
      void importMediaInterface().then(mediaInterface => {
         if ((!Chapters || Chapters.chapters.length === 0) && mediaInterface.tocChapters.length === 0) {
            setInternalChapters([]);
            return;
         }
         let mappedChapters: IChaptersDisplayModel[];
         if (Chapters && Chapters.chapters.length > 0) {
            mappedChapters = Chapters.chapters.map(chapter => ({timeCode: TimeFormatter.formatTime(chapter.timeInMilliseconds / 1000), description: chapter.label, timeCodeValid: true, descriptionValid: true} as IChaptersDisplayModel));
         } else {
            mappedChapters = mediaInterface.tocChapters.map(chapter => ({timeCode: TimeFormatter.formatTime(chapter.startTime / 1000), description: chapter.name, timeCodeValid: true, descriptionValid: true} as IChaptersDisplayModel));
            onChaptersUpdateCallback(mappedChapters);
         }
         setInternalChapters(mappedChapters);
      });
   }, [Chapters]);

   return (
      <MediaDetailsChaptersContainer
         className="form-field-group"
      >
         <label>
            {t('details.chapters.label')}
         </label>
         {internalChapters.length === 0 ? (
            <ControlButton
               className="addChapterButton"
               label={t('details.chapters.addChapterButtonLabel')}
               buttonType={TscButtonType.secondary}
               onClick={addNewChapter}
               testId="add-chapter-button"
               glyph={<Glyphs.Add16x16 />}
            />
         ) : (
            <ChaptersInputContainer theme={theme}>
               <label className="chapterTimeCodeLabel">
                  {t('details.chapters.timeCodeLabel')}
               </label>
               <label className="chapterDescriptionLabel">
                  {t('details.chapters.chapterDescriptionLabel')}
               </label>
               <ChaptersItemContainer>
                  {internalChapters.map((chapter, index): React.JSX.Element => (
                     <React.Fragment key={`chapter-${index}`}>
                        <TextInput
                           key={`timeCodeInput-${index}`}
                           value={chapter.timeCode}
                           disabled={index === 0 && chapter.timeCode === initialChapter.timeCode}
                           onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChapterTimeCodeChange(index, e.target.value)}
                           onBlur={() => validateBlurredTimeCodeInput(index)}
                           placeholder={t('details.chapters.timeCodePlaceholder')}
                           className={`chapterTimeCodeInput ${!chapter.timeCodeValid ? 'invalidChapterInput' : ''}`}
                           rows={1}
                           ariaLabel={t('details.chapters.timeCodeAriaLabel')}
                           autoFocus={index !== 0 && index === chapterIndexJustAdded}
                           testId={`chapter-time-code-${index}`}
                           tabIndex={0}
                        />
                        <TextInput
                           key={`descriptionInput-${index}`}
                           value={chapter.description}
                           onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChapterLabelChange(index, e.target.value)}
                           onBlur={() => validateBlurredDescriptionInput(index)}
                           onEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (index === internalChapters.length - 1) {
                                 e.preventDefault();
                                 addNewChapter();
                              }
                           }}
                           className={`chapterDescriptionInput ${!chapter.descriptionValid ? 'invalidChapterInput' : ''}`}
                           rows={1}
                           ariaLabel={t('details.chapters.chapterDescriptionAriaLabel')}
                           autoFocus={index === 0 && index === chapterIndexJustAdded}
                           testId={`chapter-description-${index}`}
                           tabIndex={0}
                        />
                        <ControlButton
                           key={`removeChapter-${index}`}
                           buttonType={TscButtonType.tertiary}
                           glyph={<Glyphs.CircleRemove16x16 />}
                           title="Remove Chapter"
                           ariaLabel="Remove Chapter"
                           className="removeChapterButton"
                           testId={`remove-chapter-button-${index}`}
                           onClick={() => {
                              removeChapter(index);
                           }}
                        />
                     </React.Fragment>
                  ))}
               </ChaptersItemContainer>
               <ControlButton
                  className="addChapterButton"
                  label={t('details.chapters.addChapterButtonLabel')}
                  buttonType={TscButtonType.secondary}
                  onClick={addNewChapter}
                  isFullWidth={true}
                  glyph={<Glyphs.Add16x16 />}
                  testId="add-chapter-button"
               />
            </ChaptersInputContainer>
         )}
         {timeCodeInErrorState && (
            <InputErrorText>
               {t('details.chapters.timeCodeInputError')}
            </InputErrorText>
         )}
         {descriptionInErrorState && (
            <InputErrorText>
               {t('details.chapters.descriptionInputError')}
            </InputErrorText>
         )}
      </MediaDetailsChaptersContainer>
   );
};

export interface IMediaDetailsChaptersBaseProps {
   generatedChapters: IChaptersDisplayModel[];
   onChaptersUpdateCallback(chapters: IChaptersDisplayModel[]): void;
   onChaptersInputErrorCallback?(hasError: boolean): void;
}

export interface IStateMappedProps {
   Duration: string;
   Chapters: IMediaChapters;
}

const mapStatesToProps = (
   {generatedChapters, onChaptersUpdateCallback, onChaptersInputErrorCallback}: Partial<IMediaDetailsChaptersBaseProps>,
   {Duration, Chapters}: Partial<IStateMappedProps>
): IMediaDetailsChaptersBaseProps & IStateMappedProps => ({generatedChapters, onChaptersUpdateCallback, onChaptersInputErrorCallback, Duration, Chapters});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(MediaDetailsChaptersBase);
